import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { Pool } from '../../types';
import { API_ORIGIN } from '../../config';
import { getBearerTokenForApi } from '../../utils/poolSiteAuth';
import { mapRectToContainDisplay } from '../../utils/videoOverlayCoords';
import {
  deleteDetectionLabel,
  detectionLabelFrameUrl,
  fetchDetectionLabelList,
  type DetectionLabelRow,
} from '../../services/detectionLabelTrainingService';

type Props = {
  pools: Pool[];
  selectedPoolIds: number[];
};

type TypeFilter = '' | 'false_positive' | 'missed_detection';

const TYPE_LABEL: Record<string, string> = {
  false_positive: '오검출',
  missed_detection: '미검출',
};

function poolName(pools: Pool[], id: number): string {
  const p = pools.find((x) => Number(x.id) === id);
  return p?.name || p?.code || String(id);
}

function fmtAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function hasBbox(bbox?: number[] | null): bbox is number[] {
  return (
    Array.isArray(bbox) &&
    bbox.length >= 4 &&
    bbox.slice(0, 4).every((n) => Number.isFinite(Number(n)))
  );
}

function rowKey(row: DetectionLabelRow): string {
  return `${row.pool_id}:${row.id}`;
}

const AuthThumb: React.FC<{
  path: string;
  alt: string;
  height?: number;
  onClick?: () => void;
  bbox?: number[] | null;
  frameWidth?: number | null;
  frameHeight?: number | null;
  labelType?: string;
}> = ({
  path,
  alt,
  height = 120,
  onClick,
  bbox,
  frameWidth,
  frameHeight,
  labelType,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [wrapSize, setWrapSize] = useState({ w: 1, h: height });

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    setNatural(null);
    const full = path.startsWith('http') ? path : `${API_ORIGIN}${path}`;
    void (async () => {
      try {
        const token = getBearerTokenForApi();
        const headers: HeadersInit = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(full, {
          headers,
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) {
          if (!cancelled) setFailed(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        revoked = u;
        setUrl(u);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [path]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      setWrapSize({
        w: Math.max(1, el.clientWidth),
        h: Math.max(1, el.clientHeight || height),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [height, url]);

  const boxStyle = useMemo(() => {
    if (!hasBbox(bbox) || !url) return null;
    const srcW = Math.max(
      1,
      Number(frameWidth) || natural?.w || 1280,
    );
    const srcH = Math.max(
      1,
      Number(frameHeight) || natural?.h || 720,
    );
    const [x1, y1, x2, y2] = bbox.map(Number);
    const mapped = mapRectToContainDisplay(
      x1,
      y1,
      x2,
      y2,
      srcW,
      srcH,
      wrapSize.w,
      wrapSize.h,
    );
    const isMiss = labelType === 'missed_detection';
    return {
      left: mapped.left,
      top: mapped.top,
      width: Math.max(2, mapped.width),
      height: Math.max(2, mapped.height),
      border: isMiss
        ? '2px solid rgba(41, 182, 246, 0.95)'
        : '2px dashed rgba(255, 167, 38, 0.95)',
      backgroundColor: isMiss
        ? 'rgba(41, 182, 246, 0.12)'
        : 'rgba(255, 167, 38, 0.14)',
    };
  }, [bbox, frameWidth, frameHeight, labelType, natural, url, wrapSize]);

  if (failed) {
    return (
      <Box
        sx={{
          height,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          프레임 없음
        </Typography>
      </Box>
    );
  }
  if (!url) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={22} />
      </Box>
    );
  }
  return (
    <Box
      ref={wrapRef}
      onClick={onClick}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        bgcolor: '#111',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={url}
        alt={alt}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setNatural({ w: img.naturalWidth, h: img.naturalHeight });
          }
        }}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
      {boxStyle ? (
        <Box
          sx={{
            position: 'absolute',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            ...boxStyle,
          }}
        />
      ) : null}
    </Box>
  );
};

/**
 * 저장된 오검출/미검출 라벨 목록·프레임 미리보기 (시스템 관리 · 학습 탭).
 */
const DetectionLabelBrowser: React.FC<Props> = ({ pools, selectedPoolIds }) => {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('');
  const [rows, setRows] = useState<DetectionLabelRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DetectionLabelRow | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const poolMap = useMemo(() => {
    const m = new Map<number, string>();
    pools.forEach((p) => m.set(Number(p.id), poolName(pools, Number(p.id))));
    return m;
  }, [pools]);

  const load = useCallback(async () => {
    if (!selectedPoolIds.length) {
      setRows([]);
      setSelected(new Set());
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const list = await fetchDetectionLabelList({
        poolIds: selectedPoolIds,
        labelType: typeFilter || undefined,
        limit: 120,
      });
      const next = Array.isArray(list) ? list : [];
      setRows(next);
      const alive = new Set(next.map(rowKey));
      setSelected((prev) => {
        const pruned = new Set<string>();
        prev.forEach((k) => {
          if (alive.has(k)) pruned.add(k);
        });
        return pruned;
      });
    } catch {
      setRows([]);
      setSelected(new Set());
      setError('라벨 목록을 불러오지 못함');
    } finally {
      setBusy(false);
    }
  }, [selectedPoolIds, typeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const allOn = rows.length > 0 && selected.size === rows.length;
  const someOn = selected.size > 0 && selected.size < rows.length;

  const toggleOne = (row: DetectionLabelRow) => {
    const k = rowKey(row);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(rows.map(rowKey)));
  };

  const removeRowsLocal = (keys: Set<string>) => {
    setRows((prev) => prev.filter((r) => !keys.has(rowKey(r))));
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.delete(k));
      return next;
    });
    setPreview((p) => (p && keys.has(rowKey(p)) ? null : p));
  };

  const onDelete = async (row: DetectionLabelRow) => {
    if (!window.confirm('이 라벨을 삭제?')) return;
    try {
      await deleteDetectionLabel(row.pool_id, row.id);
      removeRowsLocal(new Set([rowKey(row)]));
    } catch {
      setError('삭제 실패');
    }
  };

  const onDeleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`선택한 ${selected.size}건을 삭제?`)) return;
    setDeleting(true);
    setError(null);
    const targets = rows.filter((r) => selected.has(rowKey(r)));
    const ok = new Set<string>();
    let fail = 0;
    for (const row of targets) {
      try {
        await deleteDetectionLabel(row.pool_id, row.id);
        ok.add(rowKey(row));
      } catch {
        fail += 1;
      }
    }
    if (ok.size) removeRowsLocal(ok);
    if (fail) setError(`${fail}건 삭제 실패`);
    setDeleting(false);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          저장된 검출 라벨
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>종류</InputLabel>
          <Select
            label="종류"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="false_positive">오검출</MenuItem>
            <MenuItem value="missed_detection">미검출</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ mr: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={allOn}
              indeterminate={someOn}
              disabled={!rows.length || busy || deleting}
              onChange={(e) => toggleAll(e.target.checked)}
              inputProps={{ 'aria-label': '전체 선택' }}
            />
          }
          label={<Typography variant="body2">전체</Typography>}
        />
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={
            deleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />
          }
          disabled={busy || deleting || selected.size === 0}
          onClick={() => void onDeleteSelected()}
        >
          선택 삭제{selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
        <Button
          size="small"
          startIcon={busy ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={busy || deleting || !selectedPoolIds.length}
          onClick={() => void load()}
        >
          새로고침
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        파란 실선 = 미검출(사람인데 놓침 → 학습 시 swimmer 양성 bbox). 주황 점선 =
        오검출(잘못 잡힌 위치 → 학습 시 빈 라벨·배경). 프레임만 있고 박스가 없으면
        오검출 hard-negative로만 쓰임.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!selectedPoolIds.length ? (
        <Typography variant="body2" color="text.secondary">
          시설을 선택하면 라벨이 표시됨
        </Typography>
      ) : busy && !rows.length ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : !rows.length ? (
        <Typography variant="body2" color="text.secondary">
          저장된 라벨 없음
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 1.5,
          }}
        >
          {rows.map((row) => {
            const hasFrame = Boolean(row.frame_path);
            const typeKo = TYPE_LABEL[row.label_type] || row.label_type;
            const k = rowKey(row);
            const isPicked = selected.has(k);
            return (
              <Box
                key={k}
                sx={{
                  border: '1px solid',
                  borderColor: isPicked ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: isPicked ? 'action.selected' : 'background.paper',
                  position: 'relative',
                }}
              >
                <Checkbox
                  size="small"
                  checked={isPicked}
                  disabled={deleting}
                  onChange={() => toggleOne(row)}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{ 'aria-label': `${typeKo} 선택` }}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    zIndex: 2,
                    bgcolor: 'rgba(0,0,0,0.35)',
                    borderRadius: 0.5,
                    color: '#fff',
                    '&.Mui-checked': { color: '#90caf9' },
                    p: 0.25,
                  }}
                />
                {hasFrame ? (
                  <AuthThumb
                    path={detectionLabelFrameUrl(row.pool_id, row.id)}
                    alt={typeKo}
                    bbox={row.bbox}
                    frameWidth={row.frame_width}
                    frameHeight={row.frame_height}
                    labelType={row.label_type}
                    onClick={() => setPreview(row)}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 120,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      프레임 없음
                    </Typography>
                  </Box>
                )}
                <Box sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={typeKo}
                      color={row.label_type === 'missed_detection' ? 'info' : 'warning'}
                    />
                    {hasBbox(row.bbox) ? (
                      <Chip size="small" variant="outlined" label="bbox" />
                    ) : (
                      <Chip size="small" variant="outlined" label="프레임만" />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      size="small"
                      aria-label="삭제"
                      disabled={deleting}
                      onClick={() => void onDelete(row)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" display="block" noWrap>
                    {poolMap.get(row.pool_id) || row.pool_id} · CCTV {row.cctv_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {fmtAt(row.created_at)}
                  </Typography>
                  {row.class_name ? (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {row.class_name}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog
        open={preview != null}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {preview
              ? `${TYPE_LABEL[preview.label_type] || preview.label_type} · ${
                  poolMap.get(preview.pool_id) || preview.pool_id
                }`
              : ''}
          </Box>
          <IconButton
            aria-label="닫기"
            onClick={() => setPreview(null)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {preview?.frame_path ? (
            <AuthThumb
              path={detectionLabelFrameUrl(preview.pool_id, preview.id)}
              alt="preview"
              height={420}
              bbox={preview.bbox}
              frameWidth={preview.frame_width}
              frameHeight={preview.frame_height}
              labelType={preview.label_type}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              프레임 없음
            </Typography>
          )}
          {preview ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {fmtAt(preview.created_at)} · CCTV {preview.cctv_id}
              {preview.note ? ` · ${preview.note}` : ''}
              {hasBbox(preview.bbox)
                ? ` · bbox ${preview.bbox
                    .slice(0, 4)
                    .map((n) => Math.round(Number(n)))
                    .join(',')}`
                : ' · bbox 없음(프레임만)'}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} variant="contained">
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetectionLabelBrowser;
